import { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  handleReload = () => {
    window.location.reload();
  };

  handleClearAndReload = () => {
    // Clear any cached state that might be causing issues
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-content">
            <div className="error-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h1>Something went wrong</h1>
            <p>The app encountered an unexpected error. This has been noted and we'll look into it.</p>
            <div className="error-actions">
              <button onClick={this.handleReload} className="primary">
                Try Again
              </button>
              <button onClick={this.handleClearAndReload} className="secondary">
                Clear Data & Reload
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Error Details</summary>
                <pre>{this.state.error.message}</pre>
                <pre>{this.state.error.stack}</pre>
              </details>
            )}
          </div>
          <style>{`
            .error-boundary {
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 2rem;
              background: #0a0a0a;
              color: #ffffff;
            }
            .error-content {
              text-align: center;
              max-width: 400px;
            }
            .error-icon {
              color: #f97316;
              margin-bottom: 1.5rem;
            }
            .error-content h1 {
              font-size: 1.5rem;
              font-weight: 600;
              margin: 0 0 0.75rem;
            }
            .error-content p {
              color: #888888;
              margin: 0 0 2rem;
              line-height: 1.5;
            }
            .error-actions {
              display: flex;
              flex-direction: column;
              gap: 0.75rem;
            }
            .error-actions button {
              padding: 0.875rem 1.5rem;
              border-radius: 12px;
              font-size: 1rem;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s;
            }
            .error-actions button.primary {
              background: #f97316;
              color: white;
              border: none;
            }
            .error-actions button.primary:hover {
              background: #ea580c;
            }
            .error-actions button.secondary {
              background: transparent;
              color: #888888;
              border: 1px solid #333333;
            }
            .error-actions button.secondary:hover {
              color: #ffffff;
              border-color: #555555;
            }
            .error-details {
              margin-top: 2rem;
              text-align: left;
              color: #666666;
            }
            .error-details summary {
              cursor: pointer;
              margin-bottom: 0.5rem;
            }
            .error-details pre {
              font-size: 0.75rem;
              overflow-x: auto;
              padding: 1rem;
              background: #141414;
              border-radius: 8px;
              margin: 0.5rem 0;
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}
